<template>
    <div class="vp-poster" noautohide @click="emit('play')">
        <img v-if="image" :src="image" alt="" class="vp-poster-image" />
        <div class="vp-poster-shade-top"></div>
        <div class="vp-poster-shade-bottom"></div>

        <div class="vp-poster-header">
            <img v-if="podcast.artwork" :src="podcast.artwork" alt="" class="vp-poster-artwork" />
            <div class="vp-poster-show">
                <div class="vp-label">{{ podcast.title }}</div>
                <div v-if="episodeLine" class="vp-poster-episode">{{ episodeLine }}</div>
            </div>
            <span class="vp-spacer"></span>
            <SpringcastMark v-if="podcast.branding" :size="34" :frame-opacity="0.28" class="vp-poster-mark" />
        </div>

        <button type="button" class="vp-poster-play" :aria-label="t('Play')" @click.stop="emit('play')">
            <VideoIcon name="play" :size="34" />
        </button>

        <div class="vp-poster-info">
            <h1 class="vp-heading vp-poster-title">{{ episode.title }}</h1>
            <div class="vp-mono vp-poster-meta">
                <span>{{ formatClock(duration) }}</span>
                <template v-if="chapterCount > 0">
                    <span class="vp-dot">·</span>
                    <span>{{ chapterCount === 1 ? t("1 chapter") : t("{n} chapters", { n: chapterCount }) }}</span>
                </template>
                <template v-if="hasTranscript">
                    <span class="vp-dot">·</span>
                    <span>{{ t("Transcript") }}</span>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue"
import SpringcastMark from "../../PlayerCore/Components/SpringcastMark.vue"
import VideoIcon from "../../PlayerCore/Components/VideoIcon.vue"
import { formatClock } from "../../PlayerCore/format.js"
import { useCoreI18n } from "../../PlayerCore/useCoreI18n.js"

const props = defineProps({
    podcast: { type: Object, required: true },
    episode: { type: Object, required: true },
    image: { type: String, default: null },
    duration: { type: Number, default: 0 },
    chapterCount: { type: Number, default: 0 },
    hasTranscript: { type: Boolean, default: false },
})

const emit = defineEmits(["play"])

const t = useCoreI18n()

const episodeLine = computed(() =>
    [
        props.episode.episode > 0 ? t("Episode {n}", { n: props.episode.episode }) : null,
        props.podcast.season !== false && props.episode.season > 0 ? t("Season {n}", { n: props.episode.season }) : null,
    ]
        .filter(Boolean)
        .join(" · "),
)
</script>

<style>
.vp-poster {
    position: absolute;
    inset: 0;
    cursor: pointer;
    background: #1e1153;
}

.vp-poster-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.vp-poster-shade-top {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 148px;
    background: linear-gradient(180deg, rgba(21, 18, 13, 0.76), rgba(21, 18, 13, 0));
}

.vp-poster-shade-bottom {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 320px;
    background: linear-gradient(0deg, rgba(21, 18, 13, 0.9) 0%, rgba(21, 18, 13, 0.62) 42%, rgba(21, 18, 13, 0) 100%);
}

.vp-poster-header {
    position: absolute;
    top: 20px;
    right: 24px;
    left: 24px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.vp-poster-artwork {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border: 1px solid rgba(251, 249, 245, 0.18);
    border-radius: 6px;
    object-fit: cover;
}

.vp-poster-show {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.vp-poster-episode {
    font-size: 13px;
    color: var(--vp-muted);
}

.vp-poster-mark {
    color: var(--vp-paper);
}

.vp-poster-play {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 92px;
    height: 92px;
    margin: -46px 0 0 -46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    color: var(--vp-ink);
    background: rgba(251, 249, 245, 0.96);
    box-shadow: 0 12px 32px rgba(21, 18, 13, 0.32);
}

.vp-poster-play svg {
    margin-left: 4px;
}

.vp-poster-info {
    position: absolute;
    right: 24px;
    bottom: 92px;
    left: 24px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.vp-poster-title {
    max-width: 680px;
    margin: 0;
    font-size: 30px;
    line-height: 1.16;
    letter-spacing: -0.02em;
}

.vp-poster-meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: var(--vp-muted);
}

@media (max-width: 575px), (max-height: 360px) {
    .vp-poster-title {
        font-size: 20px;
    }

    .vp-poster-play {
        width: 64px;
        height: 64px;
        margin: -32px 0 0 -32px;
    }
}
</style>
